import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Plus, Settings2, Trash2, ArrowUp, ArrowDown, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cheap in-memory cache so re-mounted selects don't refetch on every keystroke
// in the parent form. Cleared on hard refresh — fine, defaults come from
// server anyway.
const cache = {
  values: null, // { field: [values] }
  listeners: new Set(),
};

const setCache = (next) => {
  cache.values = next;
  cache.listeners.forEach((fn) => fn(next));
};

const loadTaxonomies = async () => {
  const res = await axios.get(`${API_URL}/api/taxonomies`);
  setCache(res.data?.taxonomies || {});
  return cache.values;
};

export const useTaxonomy = (field) => {
  const [values, setValues] = useState(() => (cache.values && cache.values[field]) || []);

  useEffect(() => {
    let mounted = true;
    const onUpdate = (all) => {
      if (mounted) setValues(all[field] || []);
    };
    cache.listeners.add(onUpdate);
    if (!cache.values) {
      loadTaxonomies().catch(() => { /* silent — component still renders with defaults */ });
    } else {
      setValues(cache.values[field] || []);
    }
    return () => {
      mounted = false;
      cache.listeners.delete(onUpdate);
    };
  }, [field]);

  const addValue = useCallback(async (newValue) => {
    const clean = String(newValue || '').trim();
    if (!clean) return false;
    const current = (cache.values && cache.values[field]) || [];
    if (current.includes(clean)) return true;
    const next = [...current, clean];
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    try {
      await axios.put(
        `${API_URL}/api/admin/taxonomies/${field}`,
        { values: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const merged = { ...(cache.values || {}), [field]: next };
      setCache(merged);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save new value');
      return false;
    }
  }, [field]);

  // Replace the full value list — used by the Manage dialog for rename /
  // delete / reorder. Takes a cleaned string[] and persists it as-is.
  const replaceValues = useCallback(async (nextList) => {
    const cleaned = nextList
      .map((v) => String(v || '').trim())
      .filter((v, i, arr) => v && arr.indexOf(v) === i);
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    try {
      await axios.put(
        `${API_URL}/api/admin/taxonomies/${field}`,
        { values: cleaned },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const merged = { ...(cache.values || {}), [field]: cleaned };
      setCache(merged);
      return cleaned;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save changes');
      return null;
    }
  }, [field]);

  return { values, addValue, replaceValues };
};

// Drop-in replacement for the existing <Select /> shadcn combo. Adds a
// "+ Add new…" option at the bottom that opens a small prompt dialog; the
// entered value is persisted server-side and immediately becomes the picked
// option in this dropdown (and every other dropdown reading the same field).
const CreatableSelect = ({
  field,
  value,
  onValueChange,
  placeholder,
  triggerClassName,
  testId,
  labelize, // optional (val) => display string
  disabled,
}) => {
  const { values, addValue, replaceValues } = useTaxonomy(field);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [managing, setManaging] = useState(false);
  const [workingList, setWorkingList] = useState([]);
  const [editIdx, setEditIdx] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingManage, setSavingManage] = useState(false);

  const handleChange = (v) => {
    if (v === '__add_new__') {
      setDraft('');
      setAdding(true);
      return;
    }
    if (v === '__manage__') {
      setWorkingList(values.slice());
      setEditIdx(null);
      setEditDraft('');
      setManaging(true);
      return;
    }
    onValueChange && onValueChange(v);
  };

  const confirmAdd = async () => {
    const clean = draft.trim();
    if (!clean) { setAdding(false); return; }
    const ok = await addValue(clean);
    if (ok) {
      onValueChange && onValueChange(clean);
      toast.success(`Added "${clean}"`);
    }
    setAdding(false);
    setDraft('');
  };

  // --- Manage dialog actions (operate on local workingList, saved on confirm)
  const moveItem = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= workingList.length) return;
    const copy = workingList.slice();
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setWorkingList(copy);
  };
  const deleteItem = (i) => {
    if (!window.confirm(`Delete "${workingList[i]}"? Any existing items tagged with this value will keep the tag, but it won't appear in the dropdown anymore.`)) return;
    setWorkingList(workingList.filter((_, idx) => idx !== i));
    if (editIdx === i) { setEditIdx(null); setEditDraft(''); }
  };
  const startEdit = (i) => {
    setEditIdx(i);
    setEditDraft(workingList[i]);
  };
  const commitEdit = () => {
    if (editIdx === null) return;
    const clean = editDraft.trim();
    if (!clean) { setEditIdx(null); setEditDraft(''); return; }
    const copy = workingList.slice();
    copy[editIdx] = clean;
    setWorkingList(copy);
    setEditIdx(null);
    setEditDraft('');
  };
  const saveManage = async () => {
    setSavingManage(true);
    const result = await replaceValues(workingList);
    setSavingManage(false);
    if (result) {
      toast.success('Options updated');
      // If the currently-selected value was renamed or deleted, clear it to
      // avoid showing an invalid selection in the form.
      if (value && !result.includes(value)) {
        onValueChange && onValueChange('');
      }
      setManaging(false);
    }
  };

  return (
    <>
      <Select value={value} onValueChange={handleChange} disabled={disabled}>
        <SelectTrigger className={triggerClassName} data-testid={testId}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {values.map((v) => (
            <SelectItem key={v} value={v}>
              {labelize ? labelize(v) : v}
            </SelectItem>
          ))}
          <SelectItem value="__add_new__" className="text-primary font-medium">
            <span className="flex items-center gap-2">
              <Plus className="w-3.5 h-3.5" /> Add new…
            </span>
          </SelectItem>
          <SelectItem value="__manage__" className="text-muted-foreground">
            <span className="flex items-center gap-2">
              <Settings2 className="w-3.5 h-3.5" /> Manage options…
            </span>
          </SelectItem>
        </SelectContent>
      </Select>

      {/* Add dialog */}
      <Dialog open={adding} onOpenChange={(o) => { if (!o) setAdding(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add a new option</DialogTitle>
            <DialogDescription>
              Type the new value. It will be saved and appear in this dropdown from now on.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') confirmAdd(); if (e.key === 'Escape') setAdding(false); }}
            placeholder="e.g. team-branding"
            data-testid="creatable-select-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
            <Button onClick={confirmAdd} data-testid="creatable-select-save">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage dialog — rename, delete, reorder */}
      <Dialog open={managing} onOpenChange={(o) => { if (!o) setManaging(false); }}>
        <DialogContent className="max-w-md max-h-[85vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Manage dropdown options</DialogTitle>
            <DialogDescription>
              Rename, delete, or reorder values for <code>{field}</code>. Click <strong>Save</strong> when you're done.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-3 space-y-2" data-testid="creatable-manage-list">
            {workingList.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No options yet. Close this and use "+ Add new…" to create one.</p>
            )}
            {workingList.map((v, i) => (
              <div key={`${v}-${i}`} className="flex items-center gap-2 rounded border p-2" data-testid={`creatable-manage-row-${i}`}>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" onClick={() => moveItem(i, -1)} disabled={i === 0} aria-label="Move up">
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => moveItem(i, 1)} disabled={i === workingList.length - 1} aria-label="Move down">
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {editIdx === i ? (
                  <>
                    <Input
                      autoFocus
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') { setEditIdx(null); setEditDraft(''); } }}
                      className="h-8"
                      data-testid={`creatable-manage-edit-input-${i}`}
                    />
                    <Button size="icon" variant="ghost" onClick={commitEdit} aria-label="Save edit">
                      <Check className="w-4 h-4 text-green-600" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => { setEditIdx(null); setEditDraft(''); }} aria-label="Cancel">
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => startEdit(i)}
                      className="flex-1 text-left text-sm px-2 py-1 rounded hover:bg-muted truncate"
                      data-testid={`creatable-manage-rename-${i}`}
                      title="Click to rename"
                    >
                      {v}
                    </button>
                    <Button size="icon" variant="ghost" onClick={() => deleteItem(i)} aria-label="Delete">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="px-6 py-4">
            <Button variant="outline" onClick={() => setManaging(false)}>Cancel</Button>
            <Button onClick={saveManage} disabled={savingManage} data-testid="creatable-manage-save">
              {savingManage ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreatableSelect;
